import { MESSAGE_BODY_MAX } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button, InlineError, Panel, TextAreaField } from "../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useAnnouncementAudience, useSendAnnouncement } from "@teamna/web-client/hooks/use-messaging.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { handOffOwnerNotice } from "./shell.tsx";

/**
 * Writing to every active member at once, in a Panel over the inbox.
 *
 * Apart from the direct-message composer because the two differ in what a slip costs. A direct
 * message that goes out half-written is one apology; an announcement is forty push notifications
 * and forty people rereading it. So the button carries the recipient count, the second step
 * quotes the text back, and only the second tap sends.
 *
 * The count comes from the server rather than the member list on this side, because "active" is
 * the server's rule and the number on the button has to be the number that will be sent to.
 */

const PREVIEW_LENGTH = 200;

export function AnnouncePanel({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { label } = useLabels();
  const { business } = useActiveBusiness();
  const businessId = business?.businessId ?? null;

  const audience = useAnnouncementAudience(businessId);
  const send = useSendAnnouncement(businessId ?? "");

  const [body, setBody] = useState("");
  const [confirming, setConfirming] = useState(false);

  const trimmed = body.trim();
  const overLimit = trimmed.length > MESSAGE_BODY_MAX;
  const canSend = trimmed.length > 0 && !overLimit && businessId !== null;
  const count = audience.data?.recipientCount ?? null;
  const people = (n: number) => `${n} ${label("member", { plural: n !== 1 })}`;
  const sendLabel = count === null ? "Send" : `Send to ${people(count)}`;

  function confirm() {
    if (!canSend) return;
    send.mutate(trimmed, {
      onSuccess: (result) => {
        handOffOwnerNotice({
          title: "Announcement sent",
          message: `${people(result.recipientCount)} got it as a push notification.`,
        });
        void navigate({
          to: "/owner/inbox/$conversationId",
          params: { conversationId: result.conversationId },
        });
      },
      onError: () => setConfirming(false),
    });
  }

  return (
    <Panel
      open
      onClose={onClose}
      title="New announcement"
      subtitle={
        confirming
          ? "This cannot be unsent."
          : `Goes to every active ${label("member")} as a push notification.`
      }
      actions={
        confirming ? (
          <>
            <Button loading={send.isPending} onClick={confirm}>
              Send
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button disabled={!canSend} onClick={() => setConfirming(true)}>
              {sendLabel}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </>
        )
      }
    >
      {send.error ? <InlineError error={send.error} /> : null}

      {confirming ? (
        <>
          <p className="pb-3 text-[17px] font-semibold text-ink">
            {count === null ? "Send this announcement?" : `${sendLabel}?`}
          </p>
          <blockquote className="rounded-card bg-quiet-surface px-4 py-3.5 text-[14px] leading-6 whitespace-pre-wrap text-quiet">
            {trimmed.length > PREVIEW_LENGTH ? `${trimmed.slice(0, PREVIEW_LENGTH)}...` : trimmed}
          </blockquote>
        </>
      ) : (
        <>
          <TextAreaField
            label="Announcement"
            rows={8}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="No sessions on Thursday. We are closed for maintenance."
            hint="They cannot reply here. Replies come to their private thread with you."
            error={overLimit ? `Keep it under ${MESSAGE_BODY_MAX} characters` : undefined}
          />
          <p
            className={[
              "pt-1.5 text-right text-[12px] tabular-nums",
              overLimit ? "font-semibold text-cancelled" : "text-ink-3",
            ].join(" ")}
          >
            {trimmed.length} / {MESSAGE_BODY_MAX}
          </p>
        </>
      )}
    </Panel>
  );
}
